import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAssessmentGroupRosterQuery,
  useBulkGradeAssessmentGroupMutation,
  usePublishAssessmentGroupMutation,
} from "../../Api/attoDeskApi";
import useUser from "../../Core/hook/useUser";
import { getAvatar } from "../../utils/avatar";
import { presentDateOnly } from "../../utils/dateOnly";
import { useConfirm } from "../../Components/Confirm";

// ═══════════════════════════════════════════════════════════════════════
// Grading roster — /assessments/groups/:id/grade
//
// Full page (not a modal) so a teacher grading twelve students has room
// and can navigate away safely under an unsaved-changes guard.
//
// Data ownership:
//   • useGetAssessmentGroupRosterQuery(id) — header + roster.
//   • useBulkGradeAssessmentGroupMutation({ id, rows, publish? }) — save.
//   • usePublishAssessmentGroupMutation(id) — explicit publish.
//
// Blank-mark rule (per spec §3):
//   In state, an unset mark is the empty string "". Displayed as
//   "Not Graded". Serialised into the PUT body as `null` — NEVER 0.
//   Percentage is display-only; the server recomputes on save.
// ═══════════════════════════════════════════════════════════════════════

// ── Types the roster endpoint returns (see AssessmentGradingController@roster) ──
export interface RosterGroup {
  id: number;
  title: string;
  assessment_type: string;
  subject_name: string;
  grade_name: string | null;
  focus_name: string | null;
  date: string;
  worth: number;
  pass_threshold: number | null;
  status: LifecycleStatus;
  instructions: string | null;
  staff: { id: number; name: string } | null;
}
export interface RosterRow {
  result_id: number;
  student_id: number;
  student_name: string;
  student_avatar: string | null;
  mark: number | null;
  percentage: number | null;
  pass_fail: boolean | null;
  result_status: ResultStatus;
  comment_1: string | null;
  comment_2: string | null;
  comment_3: string | null;
  graded_by: number | null;
  graded_at: string | null;
}
export type ResultStatus = "not_graded" | "graded" | "excused" | "absent";
export type LifecycleStatus =
  | "draft" | "scheduled" | "not_graded" | "partially_graded"
  | "graded" | "published" | "cancelled";

// ── Local editable state per row ────────────────────────────────────
// `mark` is a STRING so we can distinguish "" (blank → null) from "0"
// (a legitimate mark of zero). Storing it as number|null would collapse
// these into the same value and reintroduce the blank-as-zero bug.
export interface RowState {
  result_id: number;
  student_id: number;
  student_name: string;
  student_avatar: string | null;
  mark: string; // "" = not graded, "0" = zero mark, "42" = graded
  status: ResultStatus;
  comment_1: string;
  comment_2: string;
  comment_3: string;
}

// ── Pure helpers (exported for tests) ───────────────────────────────

export const isBlank = (m: string): boolean => m.trim() === "";

/** Which pass indicator to show, based on the group's stored threshold
 * and the row's current mark. Returns null when we should render a
 * neutral dash — either the threshold is unset OR the row is blank. */
export const passFailOf = (
  markStr: string,
  worth: number,
  threshold: number | null,
): boolean | null => {
  if (isBlank(markStr) || threshold == null) return null;
  const n = Number(markStr);
  if (!Number.isFinite(n) || worth <= 0) return null;
  const pct = (n / worth) * 100;
  return pct >= threshold;
};

/** Percentage for display only; the server recomputes on save. Returns
 * null when blank so the cell can render "—" instead of "0%". */
export const displayPercentage = (markStr: string, worth: number): number | null => {
  if (isBlank(markStr) || worth <= 0) return null;
  const n = Number(markStr);
  if (!Number.isFinite(n)) return null;
  return Math.round((n / worth) * 100);
};

/** A row is "complete" for publish purposes when it has a mark OR is
 * explicitly Excused/Absent. Backend enforces the same rule via
 * resolveLifecycleStatus — this is the UI mirror. */
export const isRowComplete = (r: RowState): boolean => {
  if (r.status === "excused" || r.status === "absent") return true;
  return !isBlank(r.mark);
};

/** Marks a row for the PUT payload. Blank mark serialises as null
 * (never 0). Excused/Absent rows carry their status but leave the mark
 * alone unless the teacher entered one; the backend will null out the
 * mark when result_status is not "graded". */
export const rowToPayload = (r: RowState): Record<string, unknown> => ({
  result_id: r.result_id,
  mark: isBlank(r.mark) ? null : Number(r.mark),
  result_status:
    r.status === "excused" ? "excused"
      : r.status === "absent" ? "absent"
      : isBlank(r.mark) ? "not_graded"
      : "graded",
  comment_1: r.comment_1 || null,
  comment_2: r.comment_2 || null,
  comment_3: r.comment_3 || null,
});

/** True when the two row states differ in any field the API cares about. */
export const rowChanged = (a: RowState, b: RowState): boolean =>
  a.mark !== b.mark ||
  a.status !== b.status ||
  a.comment_1 !== b.comment_1 ||
  a.comment_2 !== b.comment_2 ||
  a.comment_3 !== b.comment_3;

/** Compute the list of rows that changed vs the baseline. */
export const diffChangedRows = (current: RowState[], baseline: RowState[]): RowState[] => {
  const byId = new Map(baseline.map((r) => [r.result_id, r]));
  return current.filter((r) => {
    const b = byId.get(r.result_id);
    return !b || rowChanged(r, b);
  });
};

export const rowFromServer = (r: RosterRow): RowState => ({
  result_id: r.result_id,
  student_id: r.student_id,
  student_name: r.student_name,
  student_avatar: r.student_avatar,
  mark: r.mark == null ? "" : String(r.mark),
  status: r.result_status,
  comment_1: r.comment_1 ?? "",
  comment_2: r.comment_2 ?? "",
  comment_3: r.comment_3 ?? "",
});

// ── Presentation constants ──────────────────────────────────────────
const READONLY_STATUSES: LifecycleStatus[] = ["published", "cancelled"];
const LIFECYCLE_LABEL: Record<LifecycleStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  not_graded: "Not Graded",
  partially_graded: "Partially Graded",
  graded: "Graded",
  published: "Published",
  cancelled: "Cancelled",
};
const LIFECYCLE_COLOR: Record<LifecycleStatus, { bg: string; fg: string; border: string }> = {
  draft:             { bg: "#f1f5f9", fg: "#475569", border: "#cbd5e1" },
  scheduled:         { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
  not_graded:        { bg: "#fff7ed", fg: "#c2410c", border: "#fed7aa" },
  partially_graded:  { bg: "#fffbeb", fg: "#a16207", border: "#fde68a" },
  graded:            { bg: "#f0fdf4", fg: "#166534", border: "#bbf7d0" },
  published:         { bg: "#eff6ff", fg: "#1e40af", border: "#bfdbfe" },
  cancelled:         { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
};

// ── The page ────────────────────────────────────────────────────────

const GradingRoster: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const groupId = idParam ? Number(idParam) : NaN;
  const navigate = useNavigate();
  const { userHasRight } = useUser();
  const canGrade = userHasRight("Assessment_Grade");
  const canPublish = userHasRight("Assessment_Publish");

  const query = useGetAssessmentGroupRosterQuery(groupId, { skip: !Number.isFinite(groupId) });
  const [bulkGrade, saveState] = useBulkGradeAssessmentGroupMutation();
  const [publishGroup, publishState] = usePublishAssessmentGroupMutation();

  const group: RosterGroup | null = (query.data?.data as any)?.group ?? null;
  const serverRoster: RosterRow[] = ((query.data?.data as any)?.roster as RosterRow[]) ?? [];

  // Baseline = last-known-server state; used for the dirty-diff so a
  // Save only sends the rows that changed. Refreshed on every successful
  // fetch and after a successful save.
  const [baseline, setBaseline] = useState<RowState[]>([]);
  const [rows, setRows] = useState<RowState[]>([]);
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);

  // Row refs for keyboard navigation between mark inputs.
  const markInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  // Load rows from server; only replace local state if we don't have
  // unsaved edits (otherwise a background refetch would clobber typing).
  const changedRows = useMemo(() => diffChangedRows(rows, baseline), [rows, baseline]);
  const isDirty = changedRows.length > 0;

  // Re-seed local state whenever the server payload identity changes
  // (RTK Query gives us a new `data` reference per fulfilled fetch).
  // Guarded so a background refetch never clobbers unsaved edits.
  useEffect(() => {
    if (!serverRoster.length) { setRows([]); setBaseline([]); return; }
    const next = serverRoster.map(rowFromServer);
    setBaseline(next);
    setRows((cur) => (cur.length && diffChangedRows(cur, next).length > 0 ? cur : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const readOnly = !canGrade
    || (group && READONLY_STATUSES.includes(group.status));

  // Focus the first ungraded row's mark input after data loads.
  useEffect(() => {
    if (readOnly || !rows.length) return;
    const first = rows.find((r) => isBlank(r.mark) && r.status !== "excused" && r.status !== "absent");
    if (first) {
      const el = markInputRefs.current.get(first.result_id);
      // Use a rAF so the input exists in the DOM before we focus.
      requestAnimationFrame(() => el?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, rows.length === 0 ? 0 : 1]);

  // ── Unsaved-changes guard on browser navigation ─────────────────
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const confirm = useConfirm();
  const back = async () => {
    if (isDirty) {
      const ok = await confirm({
        title: "Leave without saving?",
        message: "You have unsaved changes. Leave without saving?",
        confirmText: "Leave",
        cancelText: "Stay",
        variant: "warning",
      });
      if (!ok) return;
    }
    navigate("/assessments");
  };

  // ── Mutators ────────────────────────────────────────────────────
  const updateRow = (result_id: number, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r) => (r.result_id === result_id ? { ...r, ...patch } : r)));
    setSaveError(null); setSaveSuccess(null);
  };
  const toggleComments = (result_id: number) => {
    setExpandedComments((prev) => {
      const next = new Set(prev);
      if (next.has(result_id)) next.delete(result_id);
      else next.add(result_id);
      return next;
    });
  };

  // ── Keyboard navigation across mark inputs ──────────────────────
  const focusMarkAtOffset = (fromId: number, offset: 1 | -1) => {
    const gradeable = rows.filter((r) => r.status !== "excused" && r.status !== "absent");
    const idx = gradeable.findIndex((r) => r.result_id === fromId);
    if (idx === -1) return;
    const nextIdx = idx + offset;
    if (nextIdx < 0 || nextIdx >= gradeable.length) return;
    const el = markInputRefs.current.get(gradeable[nextIdx].result_id);
    if (el) { el.focus(); el.select(); }
  };
  const onMarkKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, result_id: number) => {
    if (e.key === "Enter") { e.preventDefault(); focusMarkAtOffset(result_id, 1); }
    else if (e.key === "Tab") { e.preventDefault(); focusMarkAtOffset(result_id, e.shiftKey ? -1 : 1); }
  };

  // ── Save ────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!isDirty || saveState.isLoading || !Number.isFinite(groupId)) return;
    setSaveError(null); setSaveSuccess(null);
    try {
      const res = await bulkGrade({
        id: groupId,
        rows: changedRows.map(rowToPayload),
      }).unwrap();
      setSaveSuccess(res?.message || "Saved.");
      // Rebaseline on success so the diff clears — DO NOT wipe the
      // typed values; keep them and mark them clean.
      setBaseline(rows);
    } catch (e: any) {
      const msg = e?.data?.message || e?.error || e?.message || "Failed to save.";
      const rowErrs: any[] = e?.data?.row_errors || [];
      const suffix = rowErrs.length
        ? " " + rowErrs.map((x) => `#${x.result_id}: ${x.error}`).join("; ")
        : "";
      // KEEP the typed values in `rows`; only surface the error.
      setSaveError(msg + suffix);
    }
  }, [isDirty, saveState.isLoading, groupId, bulkGrade, changedRows, rows]);

  // ── Publish ─────────────────────────────────────────────────────
  const allComplete = rows.length > 0 && rows.every(isRowComplete);
  const canPublishNow = allComplete && !isDirty && !readOnly && canPublish;
  const publishTooltip = !canPublish
    ? "You don't have permission to publish."
    : isDirty
      ? "Save your changes before publishing."
      : !allComplete
        ? "Every student must be graded, excused or absent before publishing."
        : "Publish results";

  const doPublish = async () => {
    setPublishError(null);
    setConfirmPublish(false);
    try {
      await publishGroup(groupId).unwrap();
    } catch (e: any) {
      setPublishError(e?.data?.message || e?.error || e?.message || "Failed to publish.");
    }
  };

  // ── Renders ─────────────────────────────────────────────────────
  if (!Number.isFinite(groupId)) {
    return <PageShell><EmptyMsg title="Invalid assessment id" hint="Return to the assessments list." onBack={() => navigate("/assessments")} /></PageShell>;
  }
  if (query.isLoading) {
    return <PageShell><LoadingSkeleton /></PageShell>;
  }
  if (query.isError) {
    return (
      <PageShell>
        <EmptyMsg
          title="Couldn't load the roster"
          hint={(query.error as any)?.data?.message || "There was an error fetching this assessment."}
          onBack={() => navigate("/assessments")}
          onRetry={() => query.refetch()}
        />
      </PageShell>
    );
  }
  if (!group) {
    return <PageShell><EmptyMsg title="Assessment not found" onBack={() => navigate("/assessments")} /></PageShell>;
  }

  const gradedCount = rows.filter((r) => r.status === "graded" || (!isBlank(r.mark) && r.status !== "excused" && r.status !== "absent")).length;

  return (
    <PageShell>
      {/* ── Back link + status ───────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <button
          type="button" onClick={back}
          style={{ background: "transparent", border: "none", color: "#1b3a6b", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
        >← Back to assessments</button>
        <StatusBadge status={group.status} />
      </div>

      {/* ── Header card ──────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {group.assessment_type.toUpperCase()}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#1b3a6b", margin: "4px 0 10px" }}>{group.title}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20, fontSize: 12, color: "#475569" }}>
          <MetaCol label="Subject" value={group.subject_name} />
          <MetaCol label="Grade"   value={group.grade_name || "—"} />
          <MetaCol label="Staff"   value={group.staff?.name || "—"} />
          <MetaCol label="Date"    value={presentDateOnly(group.date)} />
          <MetaCol label="Worth"   value={`${group.worth}`} />
          <MetaCol label="Pass threshold" value={
            group.pass_threshold == null
              ? <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Threshold not set</span>
              : <>{group.pass_threshold}% <span style={{ color: "#94a3b8", fontSize: 10, marginLeft: 4 }}>(group)</span></>
          } />
          <MetaCol label="Progress" value={
            <span style={{ fontWeight: 700, color: gradedCount === rows.length && rows.length ? "#16a34a" : "#1b3a6b" }}>
              {gradedCount} of {rows.length} graded
            </span>
          } />
        </div>
        {group.instructions && (
          <div style={{ marginTop: 12, padding: "10px 12px", background: "#f8fafc", borderRadius: 8, fontSize: 12, color: "#475569" }}>
            <strong>Instructions:</strong> {group.instructions}
          </div>
        )}
        {group.status === "cancelled" && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, color: "#b91c1c" }}>
            This assessment has been cancelled. Grading is read-only.
          </div>
        )}
        {group.status === "published" && (
          <div style={{ marginTop: 10, padding: "8px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, fontSize: 12, color: "#1e40af" }}>
            Results are published and visible to families. The roster is read-only.
          </div>
        )}
      </div>

      {/* ── Roster ───────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ background: "#1b3a6b", display: "grid", gridTemplateColumns: "1.8fr 120px 90px 92px 140px 100px", gap: 12, padding: "10px 16px", alignItems: "center" }}>
          {["Student", "Mark", "Percentage", "Pass/Fail", "Result Status", "Comments"].map((h) => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)", letterSpacing: "0.5px", textTransform: "uppercase" }}>{h}</div>
          ))}
        </div>

        {rows.length === 0 ? (
          <EmptyMsg title="No students assigned" hint="This assessment has no assigned students yet." onBack={back} />
        ) : rows.map((r) => (
          <RosterRowUI
            key={r.result_id}
            row={r}
            worth={group.worth}
            threshold={group.pass_threshold}
            readOnly={!!readOnly}
            expanded={expandedComments.has(r.result_id)}
            onChange={(patch) => updateRow(r.result_id, patch)}
            onToggleComments={() => toggleComments(r.result_id)}
            onMarkKeyDown={(e) => onMarkKeyDown(e, r.result_id)}
            registerMarkRef={(el) => {
              if (el) markInputRefs.current.set(r.result_id, el);
              else markInputRefs.current.delete(r.result_id);
            }}
          />
        ))}
      </div>

      {/* ── Save / Publish bar ────────────────────────────────────── */}
      {!readOnly && (
        <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {isDirty
              ? <><strong>{changedRows.length}</strong> row{changedRows.length === 1 ? "" : "s"} changed — unsaved</>
              : saveSuccess
                ? <span style={{ color: "#16a34a", fontWeight: 600 }}>{saveSuccess}</span>
                : "All changes saved"}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={save}
              disabled={!isDirty || saveState.isLoading}
              style={{
                padding: "10px 20px", borderRadius: 10, border: "none",
                background: (!isDirty || saveState.isLoading) ? "#cbd5e1" : "#1b3a6b",
                color: "#fff", fontWeight: 700, fontSize: 13,
                cursor: (!isDirty || saveState.isLoading) ? "not-allowed" : "pointer",
              }}
            >{saveState.isLoading ? "⏳ Saving…" : "💾 Save"}</button>
            {canPublish && (
              <button
                type="button"
                onClick={() => setConfirmPublish(true)}
                disabled={!canPublishNow || publishState.isLoading}
                title={publishTooltip}
                style={{
                  padding: "10px 20px", borderRadius: 10, border: "none",
                  background: (!canPublishNow || publishState.isLoading) ? "#cbd5e1" : "#16a34a",
                  color: "#fff", fontWeight: 700, fontSize: 13,
                  cursor: (!canPublishNow || publishState.isLoading) ? "not-allowed" : "pointer",
                }}
              >{publishState.isLoading ? "⏳ Publishing…" : "📢 Publish"}</button>
            )}
          </div>
        </div>
      )}

      {saveError && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, color: "#b91c1c" }}>
          {saveError}
        </div>
      )}
      {publishError && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, color: "#b91c1c" }}>
          {publishError}
        </div>
      )}

      {/* ── Publish confirmation ─────────────────────────────────── */}
      {confirmPublish && (
        <div
          onClick={() => setConfirmPublish(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "100%" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1b3a6b", marginBottom: 8 }}>Publish results?</div>
            <div style={{ fontSize: 13, color: "#475569", marginBottom: 20, lineHeight: 1.5 }}>
              Publishing makes these results visible to families through the Parent Portal
              and freezes the roster. You will need permission to re-open it for edits.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button" onClick={() => setConfirmPublish(false)}
                style={{ padding: "8px 16px", background: "transparent", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13, color: "#64748b" }}
              >Cancel</button>
              <button
                type="button" onClick={doPublish}
                style={{ padding: "8px 16px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}
              >📢 Publish</button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

// ── Sub-components (kept in-file per scope constraint) ──────────────

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 24px" }}>{children}</div>
);

const MetaCol: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
    <div style={{ fontSize: 13, color: "#1a1a2e", marginTop: 2 }}>{value}</div>
  </div>
);

const StatusBadge: React.FC<{ status: LifecycleStatus }> = ({ status }) => {
  const c = LIFECYCLE_COLOR[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700,
    }}>{LIFECYCLE_LABEL[status]}</span>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div>
    <div style={{ height: 100, background: "#f1f5f9", borderRadius: 12, marginBottom: 14 }} />
    <div style={{ height: 300, background: "#f1f5f9", borderRadius: 12 }} />
  </div>
);

const EmptyMsg: React.FC<{ title: string; hint?: string; onBack?: () => void; onRetry?: () => void }> =
  ({ title, hint, onBack, onRetry }) => (
    <div style={{ padding: 40, textAlign: "center", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#1b3a6b" }}>{title}</div>
      {hint && <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{hint}</div>}
      <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "center" }}>
        {onRetry && (
          <button type="button" onClick={onRetry} style={{ padding: "8px 16px", background: "#1b3a6b", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>Retry</button>
        )}
        {onBack && (
          <button type="button" onClick={onBack} style={{ padding: "8px 16px", background: "transparent", color: "#64748b", border: "1px solid #e2e8f0", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>Back</button>
        )}
      </div>
    </div>
  );

const RosterRowUI: React.FC<{
  row: RowState;
  worth: number;
  threshold: number | null;
  readOnly: boolean;
  expanded: boolean;
  onChange: (patch: Partial<RowState>) => void;
  onToggleComments: () => void;
  onMarkKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  registerMarkRef: (el: HTMLInputElement | null) => void;
}> = ({ row, worth, threshold, readOnly, expanded, onChange, onToggleComments, onMarkKeyDown, registerMarkRef }) => {
  const disabled = readOnly || row.status === "excused" || row.status === "absent";
  const pct = displayPercentage(row.mark, worth);
  const pf = passFailOf(row.mark, worth, threshold);
  const markCellStyle: CSSProperties = {
    width: 88, padding: "6px 8px", borderRadius: 6, border: "1.5px solid #e2e8f0",
    fontSize: 13, fontWeight: 600, textAlign: "right", boxSizing: "border-box",
    background: disabled ? "#f8fafc" : "#fff",
    color: disabled ? "#94a3b8" : "#1a1a2e", outline: "none",
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1.8fr 120px 90px 92px 140px 100px", gap: 12, alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #f1f5f9" }}>
        {/* Student */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src={getAvatar(row.student_name, null, 32)}
            alt="" width={32} height={32}
            style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
          />
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis" }}>{row.student_name}</div>
        </div>

        {/* Mark input — blank stays blank, "" in state → null in payload */}
        <div>
          <input
            ref={registerMarkRef}
            type="number"
            inputMode="numeric"
            min={0}
            max={worth}
            value={row.mark}
            disabled={disabled}
            data-testid={`mark-${row.result_id}`}
            onChange={(e) => onChange({ mark: e.target.value })}
            onKeyDown={onMarkKeyDown}
            placeholder="—"
            style={markCellStyle}
          />
          <span style={{ marginLeft: 4, fontSize: 11, color: "#94a3b8" }}>/ {worth}</span>
        </div>

        {/* Percentage — display only when a mark is entered */}
        <div style={{ fontSize: 13, fontWeight: 700, color: pct == null ? "#94a3b8" : "#1a1a2e" }}>
          {pct == null ? "—" : `${pct}%`}
        </div>

        {/* Pass/Fail — neutral dash when threshold is null OR blank */}
        <div style={{ fontSize: 12, fontWeight: 700 }}>
          {pf == null
            ? <span style={{ color: "#94a3b8" }} data-testid={`pf-${row.result_id}`}>—</span>
            : pf
              ? <span style={{ color: "#16a34a" }} data-testid={`pf-${row.result_id}`}>✓ Pass</span>
              : <span style={{ color: "#dc2626" }} data-testid={`pf-${row.result_id}`}>✗ Fail</span>}
        </div>

        {/* Result status */}
        <div>
          <select
            value={row.status}
            disabled={readOnly}
            onChange={(e) => onChange({ status: e.target.value as ResultStatus })}
            style={{
              width: "100%", padding: "6px 8px", borderRadius: 6, border: "1.5px solid #e2e8f0",
              fontSize: 12, background: "#fff", cursor: readOnly ? "not-allowed" : "pointer",
            }}
          >
            <option value="graded">Graded</option>
            <option value="not_graded">Not Graded</option>
            <option value="excused">Excused</option>
            <option value="absent">Absent</option>
          </select>
        </div>

        {/* Comments toggle */}
        <div>
          <button
            type="button" onClick={onToggleComments}
            style={{
              padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0",
              background: expanded ? "#eff6ff" : "#fff", color: "#1b3a6b",
              fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >{expanded ? "▲" : "▼"} Comments</button>
        </div>
      </div>

      {/* Expandable comments panel */}
      {expanded && (
        <div style={{ padding: "12px 16px 16px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
          {[
            { key: "comment_1", label: "The student did well on…" },
            { key: "comment_2", label: "They need to work on…" },
            { key: "comment_3", label: "To improve they need to…" },
          ].map((c) => (
            <div key={c.key} style={{ marginBottom: 8 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>{c.label}</label>
              <textarea
                rows={2}
                value={(row as any)[c.key] as string}
                disabled={readOnly}
                onChange={(e) => onChange({ [c.key]: e.target.value } as Partial<RowState>)}
                style={{
                  width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 6,
                  fontSize: 12, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
                  background: readOnly ? "#f1f5f9" : "#fff",
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GradingRoster;
